import { CircleCheck, CircleX } from 'lucide-react';
import { cx } from '../../components/charte';
import type { Letter, QuestionView } from '../../lib/roadCode';
import { RoadCodeMediaView } from './RoadCodeMediaView';
import { THEME_VISUALS } from './themeVisuals';

/** Pastille d'une lettre, à la façon du boîtier de l'examen. */
export function LetterChip({ letter, tone = 'idle' }: { letter: Letter; tone?: 'idle' | 'selected' | 'good' | 'bad' }) {
  return (
    <span
      aria-hidden
      className={cx(
        'grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold transition-colors',
        tone === 'idle' && 'bg-surface-2 text-secondary',
        tone === 'selected' && 'bg-primary text-white',
        tone === 'good' && 'bg-success text-white',
        tone === 'bad' && 'bg-danger text-white',
      )}
    >
      {letter}
    </span>
  );
}

/** État d'une proposition : choisie, ou, une fois corrigée, bonne réponse ou erreur de l'élève. */
export function choiceTone(letter: Letter, selected: Letter[], expected?: Letter[]) {
  const picked = selected.includes(letter);
  if (!expected) return picked ? 'selected' : 'idle';
  if (expected.includes(letter)) return 'good';
  return picked ? 'bad' : 'idle';
}

/**
 * Question : illustration, introduction, puis chaque partie et ses propositions. Les propositions se
 * touchent directement (en plus du boîtier du bas) ; corrigées, la bonne réponse passe en vert et
 * l'erreur en rouge, avec une icône (la couleur n'est jamais seule).
 */
export function QuestionCard({
  question,
  selected,
  expected,
  onToggle,
  locked,
}: {
  question: QuestionView;
  selected: Letter[];
  /** Bonnes réponses, une fois la question corrigée. */
  expected?: Letter[];
  onToggle?: (letter: Letter) => void;
  locked?: boolean;
}) {
  const { icon: Icon, tile } = THEME_VISUALS[question.theme];
  return (
    <div>
      {question.media ? (
        <RoadCodeMediaView media={question.media} className="sm:rounded-card" />
      ) : (
        <div className={cx('flex aspect-[3/1] w-full items-center justify-center sm:rounded-card', tile)} aria-hidden>
          <Icon size={44} strokeWidth={1.4} />
        </div>
      )}
      <div className="px-4 pt-4 sm:px-0">
        {question.intro && <p className="text-[16px] font-semibold leading-snug text-heading">{question.intro}</p>}
        {question.parts.map((part, index) => (
          <fieldset key={part.prompt} className={cx((index > 0 || question.intro) && 'mt-4')}>
            <legend className="text-[16px] font-semibold leading-snug text-heading">{part.prompt}</legend>
            <ul className="mt-2.5 grid gap-2">
              {part.answers.map((answer) => {
                const tone = choiceTone(answer.letter, selected, expected);
                return (
                  <li key={answer.letter}>
                    <button
                      type="button"
                      disabled={locked}
                      aria-pressed={selected.includes(answer.letter)}
                      onClick={() => onToggle?.(answer.letter)}
                      className={cx(
                        'flex min-h-[52px] w-full items-center gap-3 rounded-row border px-3.5 py-2.5 text-left text-[15px] transition',
                        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-default',
                        tone === 'idle' && 'border-line bg-white text-body enabled:hover:border-primary-200',
                        tone === 'selected' && 'border-primary bg-surface-indigo font-medium text-primary-700 ring-2 ring-primary-100',
                        tone === 'good' && 'border-success bg-success-light font-medium text-success-dark',
                        tone === 'bad' && 'border-danger bg-danger-light font-medium text-danger-dark',
                      )}
                    >
                      <LetterChip letter={answer.letter} tone={tone} />
                      <span className="min-w-0 flex-1">{answer.text}</span>
                      {tone === 'good' && <CircleCheck size={18} className="shrink-0" aria-label="Bonne réponse" />}
                      {tone === 'bad' && <CircleX size={18} className="shrink-0" aria-label="Votre erreur" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))}
      </div>
    </div>
  );
}
